import { useState } from 'react';
import { useDonationContextProvider } from 'context/DonationContextProvider';
import { useBranchContext } from 'context/BranchContext';

import { Button, Offcanvas, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { GiftAidCard } from './GiftAidCard';
import { CoverTransactionFee } from './CoverTransactionFee';
import { formatMoney } from 'helpers/utils';
import { DONATION_STEPS } from './data';
import { supabase } from '../../lib/supabase';
import { createDonationPaymentIntent, persistBasketSession } from '../../lib/supabaseQueries';

function GiftAidDrawer() {
  const { branchId } = useBranchContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    isGiftAidDrawerOpen,
    setIsGiftAidDrawerOpen,
    setIsAuthModalOpen,
    setAuthModalSource,
    setDonationStep,
    isDonorAuthenticated,
    donor,
    setDonor,
    basket,
    giftAid,
    totalAmount,
    subtotal,
    transactionFeeAmount,
    transactionFee,
    setClientSecret,
    setStripeKey
  } = useDonationContextProvider();

  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

  const completeDonationHandler = async () => {
    if (!isDonorAuthenticated) {
      setIsGiftAidDrawerOpen(false);
      setAuthModalSource('checkout');
      setIsAuthModalOpen(true);
      return;
    }

    // Authenticated donor — look up their record and skip to payment if found
    setIsProcessing(true);
    try {
      const email = donor?.email;
      if (!email) {
        setIsGiftAidDrawerOpen(false);
        setDonationStep(DONATION_STEPS.DONOR_INFORMATION);
        return;
      }

      const { data: donors, error: donorError } = await supabase
        .from('donars')
        .select('*')
        .eq('organisation_id', organizationId)
        .ilike('primary_email', email)
        .limit(1);

      if (donorError || !donors?.length) {
        setIsGiftAidDrawerOpen(false);
        setDonationStep(DONATION_STEPS.DONOR_INFORMATION);
        return;
      }

      const existingDonor = donors[0];
      setDonor({
        title: existingDonor.title || '',
        firstName: existingDonor.first_name || '',
        lastName: existingDonor.last_name || '',
        email: existingDonor.primary_email || email,
        phoneNumber: existingDonor.primary_phone || '',
        addressLine1: existingDonor.address?.[0]?.addressLine1 || '',
        addressLine2: existingDonor.address?.[0]?.addressLine2 || '',
        city: existingDonor.address?.[0]?.city || '',
        country: existingDonor.address?.[0]?.country || '',
        postcode: existingDonor.address?.[0]?.postcode || '',
        hearAboutUs: existingDonor.hear_about_us || '',
        type: 'personal',
        isPrimary: true
      });

      const donorObject = {
        first_name: existingDonor.first_name,
        last_name: existingDonor.last_name,
        branch_id: branchId,
        organisation_id: organizationId,
        hear_about_us: existingDonor.hear_about_us || null,
        email: existingDonor.email || [{ email, isPrimary: true }],
        primary_email: existingDonor.primary_email || email,
        phone: existingDonor.phone || [],
        primary_phone: existingDonor.primary_phone || null,
        address: existingDonor.address || [],
        primary_address: existingDonor.primary_address || null,
        type: 'individual',
        title: existingDonor.title || 'Mr'
      };

      const isRecurring = basket.some(item => item.paymentType === 'monthly');
      const orderDetails = basket.map(item => ({
        amount: item.count * item.donationAmount.value,
        billing_condition: item.paymentType,
        donation_type_id: item.donationType.id,
        package_id: item.packageId || null
      }));

      const { data, error } = await createDonationPaymentIntent({
        donorObject,
        amount: parseFloat(totalAmount),
        orderDetails,
        subtotal: parseFloat(subtotal),
        transactionFees: transactionFee ? parseFloat(transactionFeeAmount) : 0,
        giftAidEligible: giftAid,
        giftAidData: null,
        isRecurring
      });

      if (error || !data?.client_secret) {
        throw new Error(error || 'Failed to create payment intent');
      }

      setClientSecret(data.client_secret);
      if (data.publishable_key) setStripeKey(data.publishable_key);

      if (data?.donor_id) {
        await persistBasketSession({
          donorId: data.donor_id,
          basket,
          transactionFee: transactionFee ? transactionFeeAmount : 0,
          subtotal,
          totalAmount,
          branchId,
          organisationId: organizationId,
          giftAidEligible: giftAid,
          giftAidData: null,
          orderDetails,
          paymentIntentId: data.payment_intent_id
        });
      }

      setIsGiftAidDrawerOpen(false);
      setDonationStep(DONATION_STEPS.PAYMENT);
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
      console.error('Authenticated donor checkout error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Offcanvas
      show={isGiftAidDrawerOpen}
      onHide={() => setIsGiftAidDrawerOpen(false)}
      placement="end"
      backdrop="static"
      className="bg-wr-donation-primary"
      style={{ width: '90%', maxWidth: 500 }}
    >
      <Offcanvas.Header>
        <Button
          className="bg-transparent shadow-none border-0 fs-2 p-0 text-danger"
          onClick={() => {
            setIsGiftAidDrawerOpen(false);
          }}
        >
          &times;
        </Button>
      </Offcanvas.Header>
      <Offcanvas.Body className="d-flex flex-column gap-3">
        <h2 className="text-uppercase wr-donation-header2 text-white wr-donation-border-b-yellow pb-3 mb-4">
          Checkout
        </h2>
        <p className="wr-donation-body2 text-white mb-3">Help us further</p>
        <div className="mb-3">
          <GiftAidCard logoPosition="below_header" />
        </div>
        <CoverTransactionFee />
        <div className="wr-donation-summary-card d-flex flex-column gap-3">
          <header className="wr-donation-text-brand-secondary fw-bold d-flex justify-content-between align-items-center">
            <span>Subtotal</span>
            <span>{formatMoney(subtotal, '£')}</span>
          </header>
          <header className="wr-donation-text-brand-secondary fw-bold d-flex justify-content-between align-items-center">
            <span>Transaction Fee</span>
            <span>
              {formatMoney(transactionFee ? transactionFeeAmount : 0, '£')}
            </span>
          </header>
          <header className="wr-donation-text-brand-secondary fw-bold d-flex justify-content-between align-items-center">
            <span>Total</span>
            <span>{formatMoney(totalAmount, '£')}</span>
          </header>
          <Button
            className="border-0 bg-wr-donation-success-500 py-3"
            onClick={completeDonationHandler}
            disabled={isProcessing}
          >
            {isProcessing ? <Spinner size="sm" animation="border" /> : 'Proceed to Checkout >'}
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

GiftAidDrawer.propTypes = {};

export { GiftAidDrawer };
